import * as React from 'react';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';
import intl from 'react-intl-universal';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import { Popover } from 'antd';

import Loading from '@src/components/pc/Loading';
import LanguageChange from '../../components/LanguageChange';

import useTemplate from '../../model/getTemplate';
import { PcScroll } from '../../context/pcScroll';
import { CompanyType } from '../../services/getQuestionnaireTpl';

import './index.less';

const { useEffect, useCallback } = React;

const TitleWithPopover: React.FC<{ title: string }> = ({ title }) => {
  const ref = React.useRef<HTMLParagraphElement>(null);
  const [showPopover, setShowPopover] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (element) {
      // 这里的 1 是为了容错，避免因为小数导致的误判
      if (element.scrollHeight > element.clientHeight + 1) {
        setShowPopover(true);
      }
    }
  }, [title]);

  const content = (
    <p ref={ref} className="pc__detail__content__header__title">
      {title}
    </p>
  );

  return showPopover ? (
    <Popover content={title} overlayStyle={{ maxWidth: 600 }}>
      {content}
    </Popover>
  ) : (
    content
  );
};

/**
 * 问卷详情页容器组件
 * 负责整体布局、数据获取和上下文提供
 */
const Container: React.FC = ({ children }) => {
  // 获取模板数据和加载状态
  const { loading, data, getTemplate } = useTemplate();
  // 从路由参数中获取问卷token
  const { questionnaire_token } = useParams<{ questionnaire_token: string }>();
  // 判断是否为字节跳动租户
  const isBytedance = get(data, 'questionnaire_tenant') === CompanyType.Bytedance;
  // 获取问卷标题信息
  const title_info =
    get(data, 'questionnaire_template_info.title_info.i18n_name') || intl.get('title');

  // 组件挂载时获取问卷模板数据
  useEffect(() => {
    getTemplate(questionnaire_token);
  }, []);

  /**
   * 滚动到指定元素
   * 用于表单校验错误时的自动滚动
   */
  const scrollToView = useCallback((ele: HTMLElement) => {
    setTimeout(() => {
      scrollIntoViewIfNeeded(ele, {
        scrollMode: 'if-needed',
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    });
  }, []);
  return (
    <div className="pc__detail-wrapper">
      {/* 加载状态显示 */}
      {loading ? (
        <Loading />
      ) : (
        <>
          {/* 顶部背景头部 */}
          <header
            className="pc__detail__header"
            style={{
              backgroundImage: `url('${process.env.PUBLIC_URL}/pc_questionnaire_bg_1.png')`
            }}
          >
            {/* 字节跳动租户显示语言切换组件 */}
            {isBytedance && <LanguageChange />}
          </header>

          {/* 内容区域包装器 */}
          <div className="pc__detail__content-wrapper">
            {/* 内容头部区域 */}
            <div
              className="pc__detail__content__header"
              style={{
                backgroundImage: `url('${process.env.PUBLIC_URL}/pc_questionnaire_bg_2.png')`
              }}
            >
              {/* 问卷标题 - 字节跳动显示自定义标题，其他显示默认标题 */}
              <TitleWithPopover title={title_info} />
              {/* 公司名称 */}
              <p className="pc__detail__content__header__companyname">
                {get(data, 'company_name.i18n_name', '')}
              </p>
            </div>

            {/* 主要内容区域 */}
            <div className="pc__detail__content">
              <div className="pc__detail__content__questionnaireDetail">
                {/* 提供滚动上下文给子组件 */}
                <PcScroll.Provider value={{ scrollToView }}>{children}</PcScroll.Provider>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Container;
